import {useEffect, useState} from "react";
import {ErrorMessage} from "./ErrorMessage";
import {checkIndexedDB} from "@/utils/indexeddb";
import {logEvent} from "@/utils/analytics";
import {useLocalStorageAvailability} from "@/hooks/useLocalStorageAvailability";

export const ErrorBoundary = ({children}: {children: React.ReactNode}) => {
  const [isIndexedDBAvailable, setIsIndexedDBAvailable] = useState<
    boolean | null
  >(null);
  const localStorageAvailability = useLocalStorageAvailability();

  useEffect(() => {
    // If you want to test the ErrorBoundary locally, you can override the indexedDB property
    // to simulate a broken IndexedDB implementation.
    // try {
    //   Object.defineProperty(window, "indexedDB", {
    //     value: false,
    //     writable: true,
    //     configurable: true,
    //   });
    //   console.log("Successfully overrode window.indexedDB");
    // } catch (error) {
    //   console.error("Unable to override window.indexedDB", error);
    // }

    checkIndexedDB().then(setIsIndexedDBAvailable);

    const handleError = (event: ErrorEvent) => {
      if (
        event.message?.includes("Connection to Indexed Database server lost")
      ) {
        try {
          logEvent("error_boundary", {
            error: event.message,
            type: "indexedDB",
          });

          // When we detect an error indicating that the connection to the Indexed Database server is lost,
          // it might be due to corruption or inconsistencies within the IndexedDB instance or we don't have space left on the device.
          // We delete all existing IndexedDB databases to clear any corrupted data,
          // then reload the page to reinitialize a fresh, stable IndexedDB instance.
          indexedDB.databases().then((databases) => {
            databases.forEach((database) => {
              if (database.name) {
                indexedDB.deleteDatabase(database.name);
              }
            });
            window.location.reload();
          });
        } catch {
          window.location.reload();
        }
      }
    };

    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, []);

  useEffect(() => {
    // Log an event if IndexedDB is unavailable.
    if (isIndexedDBAvailable === false) {
      logEvent("error_boundary", {
        error: "indexedDB_unavailable",
        type: "indexedDB",
      });
    }

    // Log an event if localStorage is unavailable.
    if (localStorageAvailability.data === false) {
      logEvent("error_boundary", {
        error: "localStorage_unavailable",
        type: "localStorage",
      });
    }
  }, [isIndexedDBAvailable, localStorageAvailability.data]);

  if (
    isIndexedDBAvailable === null ||
    localStorageAvailability.data === undefined
  ) {
    return null;
  }

  if (
    isIndexedDBAvailable === false ||
    localStorageAvailability.data === false
  ) {
    return <ErrorMessage />;
  }

  return <>{children}</>;
};
